/**
 * Celebration Overlay — Full-screen birthday & work anniversary celebration.
 *
 * Birthday:    Canvas fireworks, animated cake with candles, personalised message.
 * Anniversary: Canvas fireworks, trophy/star animation, years counter that ticks up.
 *
 * Shown once per day per celebration type, per PERSON: localStorage for this
 * browser, plus a server flag (`seen`) so a kiosk — whose profile is wiped at
 * every sign-out — does not replay it at every sign-in on the day.
 */
import { api, store } from '@isg/ui-kit';
import { t } from '../i18n/index.js';

const LS_PREFIX = 'isg_celebration';

// ── Fireworks engine (MEGA edition) ──────────────────────────────

class Particle {
  constructor(x, y, color, velocity, life, size, type = 'normal') {
    this.x = x;
    this.y = y;
    this.color = color;
    this.vx = velocity.x;
    this.vy = velocity.y;
    this.life = life;
    this.maxLife = life;
    this.size = size;
    this.type = type;
    this.gravity = type === 'sparkle' ? 0.01 : type === 'crackle' ? 0.06 : 0.025;
    this.trail = [];
    this.flickerPhase = Math.random() * Math.PI * 2;
    this.spin = (Math.random() - 0.5) * 0.2;
  }

  update() {
    this.trail.push({ x: this.x, y: this.y });
    if (this.trail.length > (this.type === 'comet' ? 14 : 8)) this.trail.shift();
    this.x += this.vx;
    this.y += this.vy;
    this.vy += this.gravity;
    this.vx *= this.type === 'sparkle' ? 0.995 : 0.985;
    this.flickerPhase += 0.15;
    this.life--;

    // Crackle particles spawn tiny sub-particles at end of life
    if (this.type === 'crackle' && this.life === 8) {
      this._shouldCrackle = true;
    }
  }

  draw(ctx) {
    const alpha = Math.max(0, this.life / this.maxLife);
    const flicker = this.type === 'sparkle'
      ? 0.6 + 0.4 * Math.sin(this.flickerPhase)
      : 1;
    const drawAlpha = alpha * flicker;

    // Long glowing trail
    if (this.trail.length > 1) {
      ctx.beginPath();
      ctx.moveTo(this.trail[0].x, this.trail[0].y);
      for (let i = 1; i < this.trail.length; i++) {
        ctx.lineTo(this.trail[i].x, this.trail[i].y);
      }
      ctx.lineTo(this.x, this.y);
      ctx.strokeStyle = this.color.replace('1)', `${drawAlpha * 0.4})`);
      ctx.lineWidth = this.size * (this.type === 'comet' ? 1.5 : 0.8);
      ctx.stroke();
    }

    // Main particle (bigger!)
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size * (this.type === 'comet' ? 1.8 : 1.2), 0, Math.PI * 2);
    ctx.fillStyle = this.color.replace('1)', `${drawAlpha})`);
    ctx.fill();

    // Big outer glow
    const glowSize = this.size * (this.type === 'sparkle' ? 5 : this.type === 'comet' ? 6 : 4);
    ctx.beginPath();
    ctx.arc(this.x, this.y, glowSize, 0, Math.PI * 2);
    ctx.fillStyle = this.color.replace('1)', `${drawAlpha * 0.12})`);
    ctx.fill();

    // Extra bright core
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size * 0.5, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, 255, 255, ${drawAlpha * 0.6})`;
    ctx.fill();
  }
}

class Rocket {
  constructor(canvas, targetX, targetY) {
    this.x = targetX !== undefined ? targetX : canvas.width * (0.1 + Math.random() * 0.8);
    this.y = canvas.height + 10;
    this.targetY = targetY !== undefined ? targetY : canvas.height * (0.08 + Math.random() * 0.3);
    this.vy = -(6 + Math.random() * 4);
    this.vx = (Math.random() - 0.5) * 1.5;
    this.trail = [];
    this.exploded = false;
    this.color = `rgba(255, ${150 + Math.random() * 105}, ${Math.random() * 100}, 1)`;
    this.sparkTimer = 0;
  }

  update() {
    if (this.exploded) return true;
    this.trail.push({ x: this.x, y: this.y, age: 0 });
    if (this.trail.length > 20) this.trail.shift();
    this.trail.forEach(t => t.age++);
    this.x += this.vx;
    this.y += this.vy;
    this.vy *= 0.985;
    if (this.y <= this.targetY) {
      this.exploded = true;
      return true;
    }
    return false;
  }

  draw(ctx) {
    if (this.exploded) return;
    // Bright rocket trail
    for (let i = 0; i < this.trail.length; i++) {
      const t = this.trail[i];
      const a = (i / this.trail.length);
      const size = 1 + a * 2;
      ctx.beginPath();
      ctx.arc(t.x + (Math.random() - 0.5) * 2, t.y, size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 200, 100, ${a * 0.7})`;
      ctx.fill();
      // Spark scatter from trail
      if (Math.random() < 0.3) {
        ctx.beginPath();
        ctx.arc(t.x + (Math.random() - 0.5) * 8, t.y + Math.random() * 4, 1, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 150, 50, ${a * 0.4})`;
        ctx.fill();
      }
    }
    // Bright rocket head
    ctx.beginPath();
    ctx.arc(this.x, this.y, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 220, 0.95)';
    ctx.fill();
    // Head glow
    ctx.beginPath();
    ctx.arc(this.x, this.y, 10, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 200, 100, 0.2)';
    ctx.fill();
  }
}

// Golden sparkle that drifts down like glitter rain
class Sparkle {
  constructor(canvas) {
    this.x = Math.random() * canvas.width;
    this.y = -10;
    this.size = 1 + Math.random() * 2.5;
    this.vy = 0.5 + Math.random() * 1.5;
    this.vx = (Math.random() - 0.5) * 0.8;
    this.life = 200 + Math.floor(Math.random() * 150);
    this.maxLife = this.life;
    this.wobble = Math.random() * Math.PI * 2;
    this.wobbleSpeed = 0.02 + Math.random() * 0.04;
    this.color = Math.random() > 0.3
      ? `rgba(255, 215, 0, 1)`
      : `rgba(255, 255, 255, 1)`;
  }

  update() {
    this.wobble += this.wobbleSpeed;
    this.x += this.vx + Math.sin(this.wobble) * 0.5;
    this.y += this.vy;
    this.life--;
  }

  draw(ctx) {
    const alpha = Math.max(0, this.life / this.maxLife) * (0.4 + 0.6 * Math.sin(this.wobble * 3));
    // Star shape
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.wobble);
    ctx.beginPath();
    for (let i = 0; i < 4; i++) {
      const angle = (Math.PI / 2) * i;
      ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(angle) * this.size * 2, Math.sin(angle) * this.size * 2);
    }
    ctx.strokeStyle = this.color.replace('1)', `${alpha})`);
    ctx.lineWidth = 0.8;
    ctx.stroke();
    // Center dot
    ctx.beginPath();
    ctx.arc(0, 0, this.size * 0.6, 0, Math.PI * 2);
    ctx.fillStyle = this.color.replace('1)', `${alpha})`);
    ctx.fill();
    ctx.restore();
  }
}

const PALETTES = [
  ['rgba(255, 50, 80, 1)', 'rgba(255, 150, 50, 1)', 'rgba(255, 255, 80, 1)', 'rgba(255, 100, 100, 1)'],
  ['rgba(80, 200, 255, 1)', 'rgba(150, 100, 255, 1)', 'rgba(255, 100, 200, 1)', 'rgba(200, 150, 255, 1)'],
  ['rgba(100, 255, 150, 1)', 'rgba(255, 255, 100, 1)', 'rgba(255, 180, 50, 1)', 'rgba(150, 255, 200, 1)'],
  ['rgba(255, 100, 100, 1)', 'rgba(255, 200, 100, 1)', 'rgba(100, 200, 255, 1)', 'rgba(255, 255, 150, 1)'],
  ['rgba(200, 100, 255, 1)', 'rgba(255, 100, 150, 1)', 'rgba(100, 255, 200, 1)', 'rgba(255, 200, 255, 1)'],
  ['rgba(255, 215, 0, 1)', 'rgba(255, 69, 0, 1)', 'rgba(189, 27, 68, 1)', 'rgba(255, 165, 0, 1)'],
];

// Explosion types for variety
function createExplosion(x, y, particles, forceType) {
  const type = forceType || ['burst', 'ring', 'chrysanthemum', 'willow', 'double'][Math.floor(Math.random() * 5)];
  const palette = PALETTES[Math.floor(Math.random() * PALETTES.length)];

  switch (type) {
    case 'burst': {
      // Classic big spherical burst
      const count = 120 + Math.floor(Math.random() * 60);
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
        const speed = 3 + Math.random() * 5;
        const color = palette[Math.floor(Math.random() * palette.length)];
        particles.push(new Particle(x, y, color,
          { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
          100 + Math.floor(Math.random() * 60),
          2.5 + Math.random() * 3));
      }
      break;
    }
    case 'ring': {
      // Expanding ring with inner sparkles
      const ringCount = 80;
      for (let i = 0; i < ringCount; i++) {
        const angle = (Math.PI * 2 * i) / ringCount;
        const speed = 4.5 + Math.random() * 0.5;
        const color = palette[0];
        particles.push(new Particle(x, y, color,
          { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
          90 + Math.floor(Math.random() * 30),
          3 + Math.random() * 2, 'comet'));
      }
      // Inner sparkle fill
      for (let i = 0; i < 40; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 2.5;
        const color = palette[1] || palette[0];
        particles.push(new Particle(x, y, color,
          { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
          120 + Math.floor(Math.random() * 40),
          1.5 + Math.random() * 2, 'sparkle'));
      }
      break;
    }
    case 'chrysanthemum': {
      // Dense, long-trailing burst
      const count = 180 + Math.floor(Math.random() * 40);
      const color = palette[Math.floor(Math.random() * palette.length)];
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.2;
        const speed = 2 + Math.random() * 4;
        particles.push(new Particle(x, y, color,
          { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
          140 + Math.floor(Math.random() * 60),
          2 + Math.random() * 2, 'comet'));
      }
      break;
    }
    case 'willow': {
      // Drooping golden trails
      const count = 100 + Math.floor(Math.random() * 40);
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.3;
        const speed = 2.5 + Math.random() * 3;
        particles.push(new Particle(x, y, 'rgba(255, 215, 0, 1)',
          { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed - 1 },
          160 + Math.floor(Math.random() * 60),
          2 + Math.random() * 2.5, 'sparkle'));
      }
      break;
    }
    case 'double': {
      // Two-stage: outer ring then inner burst
      const ringCount = 60;
      for (let i = 0; i < ringCount; i++) {
        const angle = (Math.PI * 2 * i) / ringCount;
        const speed = 5 + Math.random() * 1;
        particles.push(new Particle(x, y, palette[0],
          { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
          70 + Math.floor(Math.random() * 30),
          3 + Math.random() * 2));
      }
      // Inner burst (different colour)
      const innerCount = 100;
      for (let i = 0; i < innerCount; i++) {
        const angle = (Math.PI * 2 * i) / innerCount + (Math.random() - 0.5) * 0.5;
        const speed = 1.5 + Math.random() * 3;
        const color = palette[2] || palette[1];
        particles.push(new Particle(x, y, color,
          { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
          120 + Math.floor(Math.random() * 50),
          2 + Math.random() * 2.5));
      }
      break;
    }
  }
}

// Grand finale: massive volley of explosions across the screen
function grandFinale(canvas, particles) {
  const positions = [];
  const cols = 5;
  const rows = 2;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      positions.push({
        x: canvas.width * (0.1 + (c / (cols - 1)) * 0.8),
        y: canvas.height * (0.15 + r * 0.25),
      });
    }
  }
  // Stagger them slightly
  positions.forEach((pos, i) => {
    setTimeout(() => {
      createExplosion(pos.x, pos.y, particles, ['burst', 'chrysanthemum', 'ring'][i % 3]);
    }, i * 80);
  });
}

function runFireworks(canvas, duration = 15000) {
  const ctx = canvas.getContext('2d');
  const particles = [];
  const rockets = [];
  const sparkles = [];
  let lastRocket = 0;
  let lastSparkle = 0;
  let screenFlash = 0;
  let finaleTriggered = false;
  const startTime = Date.now();
  let animId;

  function resize() {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }
  resize();
  window.addEventListener('resize', resize);

  function frame() {
    const elapsed = Date.now() - startTime;

    // Slight persistence for glow trails (don't fully clear)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Clear more aggressively to prevent too much blur
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Screen flash effect (white overlay that fades)
    if (screenFlash > 0) {
      ctx.fillStyle = `rgba(255, 255, 255, ${screenFlash})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      screenFlash *= 0.85;
      if (screenFlash < 0.01) screenFlash = 0;
    }

    // ── Golden sparkle rain (constant gentle background) ──
    if (elapsed - lastSparkle > 40 && elapsed < duration - 1000) {
      sparkles.push(new Sparkle(canvas));
      if (Math.random() < 0.5) sparkles.push(new Sparkle(canvas));
      lastSparkle = elapsed;
    }

    for (let i = sparkles.length - 1; i >= 0; i--) {
      sparkles[i].update();
      if (sparkles[i].life <= 0 || sparkles[i].y > canvas.height + 20) {
        sparkles.splice(i, 1);
      } else {
        sparkles[i].draw(ctx);
      }
    }

    // ── Launch rockets ──
    // Phase 1 (0-4s): rapid fire, 2-3 at a time
    // Phase 2 (4-10s): steady stream
    // Phase 3 (10-12s): build-up — faster again
    // Grand finale (12-13s): everything at once
    const phase = elapsed < 4000 ? 'opening' : elapsed < 10000 ? 'steady' : elapsed < 12000 ? 'buildup' : 'finale';

    let interval;
    let rocketBatch;
    switch (phase) {
      case 'opening':  interval = 180; rocketBatch = 3; break;
      case 'steady':   interval = 350; rocketBatch = 2; break;
      case 'buildup':  interval = 120; rocketBatch = 4; break;
      case 'finale':   interval = 9999; rocketBatch = 0; break;
    }

    if (phase !== 'finale' && elapsed - lastRocket > interval) {
      for (let b = 0; b < rocketBatch; b++) {
        rockets.push(new Rocket(canvas));
      }
      lastRocket = elapsed;
    }

    // Grand finale
    if (phase === 'finale' && !finaleTriggered) {
      finaleTriggered = true;
      grandFinale(canvas, particles);
      screenFlash = 0.4;
    }

    // Update rockets
    for (let i = rockets.length - 1; i >= 0; i--) {
      const r = rockets[i];
      if (r.update()) {
        if (r.exploded) {
          createExplosion(r.x, r.y, particles);
          screenFlash = Math.max(screenFlash, 0.08 + Math.random() * 0.08);
        }
        rockets.splice(i, 1);
      } else {
        r.draw(ctx);
      }
    }

    // Update particles
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.update();
      if (p.life <= 0) {
        // Crackle effect: spawn tiny secondary particles
        if (p._shouldCrackle || (p.type === 'normal' && Math.random() < 0.02)) {
          for (let j = 0; j < 4; j++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 0.5 + Math.random() * 1;
            particles.push(new Particle(p.x, p.y, p.color,
              { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
              20 + Math.floor(Math.random() * 15),
              1, 'crackle'));
          }
        }
        particles.splice(i, 1);
      } else {
        p.draw(ctx);
      }
    }

    if (elapsed < duration + 3000 || particles.length > 0 || sparkles.length > 0) {
      animId = requestAnimationFrame(frame);
    }
  }

  frame();

  return () => {
    cancelAnimationFrame(animId);
    window.removeEventListener('resize', resize);
  };
}


// ── Confetti burst (lighter, for the dismiss transition) ─────────

function burstConfetti(container, count = 80) {
  for (let i = 0; i < count; i++) {
    const el = document.createElement('div');
    const size = 6 + Math.random() * 8;
    const colors = ['#BD1B44', '#FFD700', '#4FC3F7', '#FF6F61', '#7C4DFF', '#00E676', '#FF9800'];
    el.style.cssText = `
      position: absolute;
      width: ${size}px; height: ${size * (0.5 + Math.random())}px;
      background: ${colors[Math.floor(Math.random() * colors.length)]};
      border-radius: ${Math.random() > 0.5 ? '50%' : '2px'};
      left: 50%; top: 45%;
      pointer-events: none;
      z-index: 10002;
    `;
    container.appendChild(el);

    const angle = Math.random() * Math.PI * 2;
    const velocity = 4 + Math.random() * 8;
    const vx = Math.cos(angle) * velocity;
    const vy = Math.sin(angle) * velocity - 3;
    const rotation = Math.random() * 720 - 360;

    el.animate([
      { transform: 'translate(-50%, -50%) rotate(0deg)', opacity: 1 },
      { transform: `translate(calc(-50% + ${vx * 60}px), calc(-50% + ${vy * 60 + 200}px)) rotate(${rotation}deg)`, opacity: 0 },
    ], { duration: 1500 + Math.random() * 1000, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', fill: 'forwards' });

    setTimeout(() => el.remove(), 2500);
  }
}


// ── Birthday cake SVG ────────────────────────────────────────────

function cakeSVG() {
  return `<svg viewBox="0 0 200 220" width="180" height="198" class="celebration-cake">
    <!-- Candle flames (animated) -->
    <g class="candle-flames">
      <ellipse cx="60" cy="42" rx="6" ry="10" fill="#FFD700" opacity="0.9">
        <animate attributeName="ry" values="10;12;9;11;10" dur="0.8s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.9;1;0.8;1;0.9" dur="0.6s" repeatCount="indefinite"/>
      </ellipse>
      <ellipse cx="100" cy="38" rx="7" ry="11" fill="#FF9800" opacity="0.9">
        <animate attributeName="ry" values="11;9;12;10;11" dur="0.7s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.9;0.8;1;0.85;0.9" dur="0.5s" repeatCount="indefinite"/>
      </ellipse>
      <ellipse cx="140" cy="42" rx="6" ry="10" fill="#FFD700" opacity="0.9">
        <animate attributeName="ry" values="9;11;10;12;9" dur="0.9s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="1;0.9;0.85;1;1" dur="0.7s" repeatCount="indefinite"/>
      </ellipse>
    </g>

    <!-- Candle glow -->
    <circle cx="60" cy="42" r="14" fill="rgba(255,200,50,0.2)">
      <animate attributeName="r" values="14;18;14" dur="1s" repeatCount="indefinite"/>
    </circle>
    <circle cx="100" cy="38" r="16" fill="rgba(255,150,50,0.2)">
      <animate attributeName="r" values="16;20;16" dur="0.8s" repeatCount="indefinite"/>
    </circle>
    <circle cx="140" cy="42" r="14" fill="rgba(255,200,50,0.2)">
      <animate attributeName="r" values="14;18;14" dur="1.1s" repeatCount="indefinite"/>
    </circle>

    <!-- Candles -->
    <rect x="56" y="52" width="8" height="38" rx="2" fill="#FF6B6B"/>
    <rect x="56" y="52" width="4" height="38" rx="1" fill="#FF8A8A"/>
    <rect x="96" y="48" width="8" height="42" rx="2" fill="#4FC3F7"/>
    <rect x="96" y="48" width="4" height="42" rx="1" fill="#81D4FA"/>
    <rect x="136" y="52" width="8" height="38" rx="2" fill="#CE93D8"/>
    <rect x="136" y="52" width="4" height="38" rx="1" fill="#E1BEE7"/>

    <!-- Top tier -->
    <rect x="30" y="90" width="140" height="45" rx="8" fill="#F8BBD0"/>
    <rect x="30" y="90" width="140" height="12" rx="6" fill="#F48FB1"/>
    <!-- Icing drips -->
    <ellipse cx="50" cy="102" rx="8" ry="12" fill="#F48FB1"/>
    <ellipse cx="80" cy="102" rx="6" ry="16" fill="#F48FB1"/>
    <ellipse cx="120" cy="102" rx="7" ry="14" fill="#F48FB1"/>
    <ellipse cx="155" cy="102" rx="8" ry="10" fill="#F48FB1"/>

    <!-- Bottom tier -->
    <rect x="15" y="135" width="170" height="55" rx="10" fill="#CE93D8"/>
    <rect x="15" y="135" width="170" height="14" rx="7" fill="#BA68C8"/>
    <!-- Icing drips -->
    <ellipse cx="35" cy="149" rx="8" ry="15" fill="#BA68C8"/>
    <ellipse cx="70" cy="149" rx="10" ry="18" fill="#BA68C8"/>
    <ellipse cx="105" cy="149" rx="7" ry="13" fill="#BA68C8"/>
    <ellipse cx="140" cy="149" rx="9" ry="17" fill="#BA68C8"/>
    <ellipse cx="170" cy="149" rx="7" ry="11" fill="#BA68C8"/>

    <!-- Decorations -->
    <circle cx="45" cy="165" r="4" fill="#FFD700"/>
    <circle cx="75" cy="170" r="3" fill="#FF6F61"/>
    <circle cx="100" cy="163" r="4" fill="#4FC3F7"/>
    <circle cx="130" cy="168" r="3" fill="#FFD700"/>
    <circle cx="155" cy="165" r="4" fill="#FF6F61"/>

    <!-- Plate -->
    <ellipse cx="100" cy="192" rx="95" ry="10" fill="#E0E0E0"/>
    <ellipse cx="100" cy="190" rx="95" ry="10" fill="#F5F5F5"/>
  </svg>`;
}


// ── Trophy SVG (for anniversary) ─────────────────────────────────

function trophySVG() {
  return `<svg viewBox="0 0 200 220" width="160" height="176" class="celebration-trophy">
    <!-- Glow -->
    <circle cx="100" cy="90" r="70" fill="rgba(255,215,0,0.08)">
      <animate attributeName="r" values="70;80;70" dur="2s" repeatCount="indefinite"/>
    </circle>

    <!-- Trophy cup -->
    <path d="M55 40 h90 l-10 80 c-2 15 -28 25 -35 25 -7 0 -33 -10 -35 -25 z"
          fill="#FFD700" stroke="#DAA520" stroke-width="2"/>
    <path d="M55 40 h90 l-5 40 h-80 z" fill="#FFEB3B" opacity="0.4"/>

    <!-- Handles -->
    <path d="M55 50 c-25 0 -30 20 -20 40 c8 15 20 15 25 10" fill="none" stroke="#DAA520" stroke-width="5" stroke-linecap="round"/>
    <path d="M145 50 c25 0 30 20 20 40 c-8 15 -20 15 -25 10" fill="none" stroke="#DAA520" stroke-width="5" stroke-linecap="round"/>

    <!-- Star on trophy -->
    <polygon points="100,55 106,72 124,72 110,82 115,99 100,89 85,99 90,82 76,72 94,72"
             fill="#FFF9C4" stroke="#FFD700" stroke-width="1"/>
    <animateTransform attributeType="xml" attributeName="transform" type="rotate"
      values="0 100 77;3 100 77;-3 100 77;0 100 77" dur="3s" repeatCount="indefinite"/>

    <!-- Stem -->
    <rect x="90" y="145" width="20" height="30" rx="3" fill="#DAA520"/>
    <rect x="93" y="145" width="8" height="30" rx="2" fill="#FFEB3B" opacity="0.3"/>

    <!-- Base -->
    <rect x="65" y="175" width="70" height="12" rx="4" fill="#DAA520"/>
    <rect x="65" y="175" width="70" height="5" rx="3" fill="#FFEB3B" opacity="0.3"/>
    <rect x="75" y="187" width="50" height="8" rx="3" fill="#B8860B"/>

    <!-- Sparkles -->
    <g class="trophy-sparkles">
      <circle cx="50" cy="35" r="2" fill="#FFD700" opacity="0">
        <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" begin="0s"/>
        <animate attributeName="r" values="1;3;1" dur="1.5s" repeatCount="indefinite" begin="0s"/>
      </circle>
      <circle cx="150" cy="30" r="2" fill="#FFD700" opacity="0">
        <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" begin="0.5s"/>
        <animate attributeName="r" values="1;3;1" dur="1.5s" repeatCount="indefinite" begin="0.5s"/>
      </circle>
      <circle cx="170" cy="70" r="2" fill="#FFD700" opacity="0">
        <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" begin="1s"/>
        <animate attributeName="r" values="1;3;1" dur="1.5s" repeatCount="indefinite" begin="1s"/>
      </circle>
      <circle cx="30" cy="75" r="2" fill="#FFD700" opacity="0">
        <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" begin="0.3s"/>
        <animate attributeName="r" values="1;3;1" dur="1.5s" repeatCount="indefinite" begin="0.3s"/>
      </circle>
    </g>
  </svg>`;
}


// ── Year counter animation ───────────────────────────────────────

function animateCounter(el, target, duration = 2000) {
  const start = Date.now();
  function tick() {
    const elapsed = Date.now() - start;
    const progress = Math.min(1, elapsed / duration);
    // Ease out cubic
    const eased = 1 - Math.pow(1 - progress, 3);
    const current = Math.round(eased * target);
    el.textContent = current;
    if (progress < 1) requestAnimationFrame(tick);
  }
  tick();
}


// ── Build the overlay DOM ────────────────────────────────────────

function buildOverlay(type, data) {
  const overlay = document.createElement('div');
  overlay.className = 'celebration-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-label', type === 'birthday'
    ? t('celebrations.overlay.aria_birthday') : t('celebrations.overlay.aria_anniversary'));

  // Canvas for fireworks
  const canvas = document.createElement('canvas');
  canvas.className = 'celebration-canvas';
  overlay.appendChild(canvas);

  // Center card
  const card = document.createElement('div');
  card.className = 'celebration-card';

  if (type === 'birthday') {
    card.innerHTML = `
      <div class="celebration-icon celebration-icon-bounce">
        ${cakeSVG()}
      </div>
      <h1 class="celebration-title celebration-title-birthday">
        ${data.first_name
          ? t('celebrations.overlay.birthday_title_named', { name: data.first_name })
          : t('celebrations.overlay.birthday_title')}
      </h1>
      <p class="celebration-subtitle">
        ${t('celebrations.overlay.birthday_message')}
      </p>
      <div class="celebration-emoji-row">
        <span class="celebration-emoji" style="animation-delay:0.1s">&#127880;</span>
        <span class="celebration-emoji" style="animation-delay:0.2s">&#127874;</span>
        <span class="celebration-emoji" style="animation-delay:0.3s">&#127881;</span>
        <span class="celebration-emoji" style="animation-delay:0.4s">&#127882;</span>
        <span class="celebration-emoji" style="animation-delay:0.5s">&#127880;</span>
      </div>
      <button class="btn btn-primary celebration-dismiss" id="celebration-dismiss">${t('celebrations.overlay.thank_you')}</button>
    `;
  } else {
    card.innerHTML = `
      <div class="celebration-icon celebration-icon-bounce">
        ${trophySVG()}
      </div>
      <h1 class="celebration-title celebration-title-anniversary">
        ${data.first_name
          ? t('celebrations.overlay.anniversary_title_named', { name: data.first_name })
          : t('celebrations.overlay.anniversary_title')}
      </h1>
      <div class="celebration-counter-wrap">
        <span class="celebration-counter" id="celebration-years">0</span>
        <span class="celebration-counter-label">${t('celebrations.overlay.years_label', { count: Number(data.years) || 0 })}</span>
      </div>
      <p class="celebration-subtitle celebration-subtitle-anni">
        ${t('celebrations.overlay.anniversary_message')}
      </p>
      <div class="celebration-emoji-row">
        <span class="celebration-emoji" style="animation-delay:0.1s">&#11088;</span>
        <span class="celebration-emoji" style="animation-delay:0.2s">&#127942;</span>
        <span class="celebration-emoji" style="animation-delay:0.3s">&#127881;</span>
        <span class="celebration-emoji" style="animation-delay:0.4s">&#129395;</span>
        <span class="celebration-emoji" style="animation-delay:0.5s">&#11088;</span>
      </div>
      <button class="btn btn-primary celebration-dismiss" id="celebration-dismiss">${t('celebrations.overlay.thank_you')}</button>
    `;
  }

  overlay.appendChild(card);
  return overlay;
}


// ── Inject styles ────────────────────────────────────────────────

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    .celebration-overlay {
      position: fixed;
      inset: 0;
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(8px);
      animation: celebrationFadeIn 0.6s ease-out;
      overflow: hidden;
    }

    .celebration-overlay.closing {
      animation: celebrationFadeOut 0.8s ease-in forwards;
    }

    .celebration-canvas {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    }

    .celebration-card {
      position: relative;
      z-index: 10001;
      text-align: center;
      padding: 3rem 3.5rem;
      border-radius: 24px;
      background: radial-gradient(ellipse at top, rgba(255,255,255,0.12), rgba(255,255,255,0.04));
      border: 1px solid rgba(255, 255, 255, 0.15);
      box-shadow: 0 25px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1);
      max-width: 480px;
      width: 90%;
      animation: celebrationCardIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both;
    }

    .celebration-icon {
      margin-bottom: 1rem;
    }

    .celebration-icon-bounce {
      animation: celebrationBounce 1s ease-out 0.6s both;
    }

    .celebration-title {
      font-size: 2.4rem;
      font-weight: 800;
      margin: 0 0 0.75rem 0;
      line-height: 1.2;
      animation: celebrationTextIn 0.6s ease-out 0.9s both;
    }

    .celebration-title-birthday {
      background: linear-gradient(135deg, #FF6B6B, #FFD93D, #6BCB77, #4D96FF, #FF6B6B);
      background-size: 300% 300%;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: celebrationTextIn 0.6s ease-out 0.9s both, celebrationRainbow 4s ease infinite;
    }

    .celebration-title-anniversary {
      background: linear-gradient(135deg, #FFD700, #FFA000, #FFD700);
      background-size: 200% 200%;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: celebrationTextIn 0.6s ease-out 0.9s both, celebrationGoldShimmer 3s ease infinite;
    }

    .celebration-subtitle {
      color: rgba(255, 255, 255, 0.85);
      font-size: 1.05rem;
      line-height: 1.6;
      margin: 0 0 1.25rem 0;
      animation: celebrationTextIn 0.6s ease-out 1.1s both;
    }

    .celebration-subtitle-anni {
      margin-top: 0.5rem;
    }

    .celebration-counter-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 0.5rem 0 0.75rem;
      animation: celebrationTextIn 0.6s ease-out 1s both;
    }

    .celebration-counter {
      font-size: 5rem;
      font-weight: 900;
      line-height: 1;
      background: linear-gradient(180deg, #FFD700 0%, #FF8C00 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      filter: drop-shadow(0 2px 8px rgba(255, 200, 0, 0.4));
    }

    .celebration-counter-label {
      font-size: 1.1rem;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.7);
      text-transform: uppercase;
      letter-spacing: 0.2em;
      margin-top: 0.25rem;
    }

    .celebration-emoji-row {
      display: flex;
      justify-content: center;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
      font-size: 1.8rem;
    }

    .celebration-emoji {
      display: inline-block;
      animation: celebrationEmojiBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      animation-delay: var(--delay, 0s);
    }

    .celebration-dismiss {
      font-size: 1rem;
      padding: 0.75rem 2.5rem;
      border-radius: 50px;
      font-weight: 600;
      cursor: pointer;
      animation: celebrationTextIn 0.6s ease-out 1.3s both;
      transition: transform 0.2s, box-shadow 0.2s;
    }

    .celebration-dismiss:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 20px rgba(189, 27, 68, 0.4);
    }

    .celebration-cake,
    .celebration-trophy {
      filter: drop-shadow(0 4px 20px rgba(255, 200, 50, 0.3));
    }

    /* ── Keyframes ── */

    @keyframes celebrationFadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }

    @keyframes celebrationFadeOut {
      from { opacity: 1; }
      to   { opacity: 0; }
    }

    @keyframes celebrationCardIn {
      from { opacity: 0; transform: scale(0.6) translateY(40px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }

    @keyframes celebrationBounce {
      0%   { opacity: 0; transform: scale(0) translateY(30px); }
      60%  { opacity: 1; transform: scale(1.15) translateY(-10px); }
      80%  { transform: scale(0.95) translateY(2px); }
      100% { transform: scale(1) translateY(0); }
    }

    @keyframes celebrationTextIn {
      from { opacity: 0; transform: translateY(15px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @keyframes celebrationEmojiBounce {
      0%   { opacity: 0; transform: scale(0) translateY(20px); }
      60%  { transform: scale(1.3) translateY(-5px); }
      100% { opacity: 1; transform: scale(1) translateY(0); }
    }

    @keyframes celebrationRainbow {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    @keyframes celebrationGoldShimmer {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    /* Responsive */
    @media (max-width: 480px) {
      .celebration-card {
        padding: 2rem 1.5rem;
      }
      .celebration-title {
        font-size: 1.8rem;
      }
      .celebration-counter {
        font-size: 3.5rem;
      }
      .celebration-cake,
      .celebration-trophy {
        width: 120px;
        height: auto;
      }
    }
  `;
  document.head.appendChild(style);
}


// ── Public API ───────────────────────────────────────────────────

/**
 * Check for celebrations and show overlay if applicable.
 * Call this once after authentication is confirmed.
 */
export async function checkCelebrations() {
  // Respect reduced motion preference
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const user = store.get('user');
  if (!user) return;

  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  try {
    const data = await api.get('/hr/my/celebrations');
    if (!data) return;

    const seen = data.seen || {};

    // First sighting today, on this browser AND for this person anywhere.
    // Marked as it is shown — never by the server's check itself.
    const claim = (type) => {
      const key = `${LS_PREFIX}_${type}_${user.id}_${today}`;
      if (localStorage.getItem(key) || seen[type]) return false;
      localStorage.setItem(key, '1');
      api.post('/hr/my/celebrations/seen', { type }).catch(() => {});
      return true;
    };

    // Show birthday first, then anniversary (if both, show sequentially)
    const showBirthday = data.birthday && claim('birthday');
    if (showBirthday) await showCelebration('birthday', data.birthday);

    if (data.anniversary && claim('anniversary')) {
      // Small delay if birthday was also shown
      const delay = showBirthday ? 1500 : 0;
      setTimeout(() => showCelebration('anniversary', data.anniversary), delay);
    }
  } catch (e) {
    // Silently fail — celebrations are non-critical
    console.debug('Celebrations check skipped:', e.message);
  }
}

function showCelebration(type, data) {
  return new Promise((resolve) => {
    injectStyles();

    const overlay = buildOverlay(type, data);
    document.body.appendChild(overlay);

    // Start fireworks (15s show with grand finale at 12s)
    const canvas = overlay.querySelector('.celebration-canvas');
    const stopFireworks = runFireworks(canvas, 15000);

    // Start year counter for anniversary
    if (type === 'anniversary' && data.years) {
      setTimeout(() => {
        const counterEl = overlay.querySelector('#celebration-years');
        if (counterEl) animateCounter(counterEl, data.years, Math.min(2500, data.years * 200));
      }, 1200);
    }

    // Dismiss handler
    function dismiss() {
      burstConfetti(overlay);
      stopFireworks();
      overlay.classList.add('closing');
      setTimeout(() => {
        overlay.remove();
        resolve();
      }, 800);
    }

    overlay.querySelector('#celebration-dismiss').addEventListener('click', dismiss);

    // Also dismiss on Escape
    function onKey(e) {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', onKey);
        dismiss();
      }
    }
    document.addEventListener('keydown', onKey);

    // Auto-dismiss after 20 seconds
    setTimeout(() => {
      if (document.body.contains(overlay) && !overlay.classList.contains('closing')) {
        dismiss();
      }
    }, 20000);
  });
}
